import { Editor } from '@tiptap/core';
import { ReactComponent as CopyIcon } from 'assets/icons/copy.svg';
import { ReactComponent as InsertColumnLeftIcon } from 'assets/icons/insert_column_left.svg';
import { ReactComponent as InsertColumnRightIcon } from 'assets/icons/insert_column_right.svg';
import { ReactComponent as InsertRowAboveIcon } from 'assets/icons/insert_row_above.svg';
import { ReactComponent as InsertRowBelowIcon } from 'assets/icons/insert_row_below.svg';
import { ReactComponent as PasteIcon } from 'assets/icons/paste.svg';
import { ReactComponent as DeleteColIcon } from 'assets/icons/table_delete_col.svg';
import { ReactComponent as DeleteRowIcon } from 'assets/icons/table_delete_row.svg';
import { isBrowser } from 'constants/browser';
import { ContextMenuItem } from 'modules/Editor/components/ContextMenu/ContextMenu.types';
import { setKeyboardShortcutModalOpen } from 'modules/Editor/store/editorSettingsSlice';
import { Slice } from 'prosemirror-model';
import {
  // @ts-ignore (incorrect type)
  __parseFromClipboard as parseFromClipboard, // @ts-ignore (incorrect type)
  __serializeForClipboard as serializeForClipboard,
} from 'prosemirror-view';

import { checkDisabledActions, fireMPonTableDelete, fireMPonTableInsert, handlePaste } from './TableWidget.helpers';
import { TableWidgetData } from './TableWidget.types';

const isCopyPasteSupported = isBrowser.CHROME;

/**
 * Generates context menu items for table widget
 * - Handles click events for updating rows + cols
 * - Handles disabled states
 *
 * @param editor Prosemirror editor
 * @param dispatchUpdateWidget widget update function
 * @param forceEnableAll [optional] if true, checkIsDisabled fns will return false for all items
 */
export const generateTableContextMenuItems = (
  editor: Editor | null,
  dispatchUpdateWidget: (options?: Partial<TableWidgetData>) => void,
  forceEnableAll?: boolean,
): ContextMenuItem[] => {
  if (!editor) return [];

  const { isAddColumnDisabled, isAddRowDisabled, isDeleteColumnDisabled, isDeleteRowDisabled } =
    checkDisabledActions(editor);

  const contextMenuItems: ContextMenuItem[] = [
    // Prosemirror copy fn
    // @see https://github.com/ProseMirror/prosemirror-view/blob/ed08c8bbbe99d0d30c0e8bccda9b79498b3adfd8/src/input.ts#L553
    {
      id: 'copy',
      onClick: ({ globalDispatch }) => {
        // Display keyboard shortcut modal if copy/paste not supported
        if (!isCopyPasteSupported) {
          globalDispatch?.(setKeyboardShortcutModalOpen(true));
          return;
        }

        const view = editor.view;
        let selection = view.state.selection;
        if (selection.empty) return;

        // Write serialized HTML + text to clipboard
        let slice = selection.content(),
          { dom, text } = serializeForClipboard(view, slice);
        navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([dom.innerHTML], { type: 'text/html' }),
          }),
        ]);
        navigator.clipboard.writeText(text);
      },
      label: 'copy',
      icon: <CopyIcon />,
      shortcuts: {
        macOs: ['⌘', '+', 'C'],
        others: ['ctrl', '+', 'C'],
      },
    },
    // Prosemirror paste fn
    // @see https://github.com/ProseMirror/prosemirror-view/blob/ed08c8bbbe99d0d30c0e8bccda9b79498b3adfd8/src/input.ts#L604
    {
      id: 'paste',
      onClick: async ({ globalDispatch }) => {
        // Display keyboard shortcut modal if copy/paste not supported
        if (!isCopyPasteSupported) {
          globalDispatch?.(setKeyboardShortcutModalOpen(true));
          return;
        }

        function sliceSingleNode(slice: Slice) {
          return slice.openStart === 0 && slice.openEnd === 0 && slice.content.childCount === 1
            ? slice.content.firstChild
            : null;
        }

        try {
          const clipboardItems = await navigator.clipboard.read();
          if (!clipboardItems?.length) return;
          const view = editor.view;

          // Get copied text + html from clipboard
          const copiedText = await navigator.clipboard.readText();
          let copiedHtml: string | null = null;
          for (const item of clipboardItems) {
            if (!item.types.includes('text/html')) continue;
            const blob = await item.getType('text/html');
            copiedHtml = await blob.text();
          }

          // Position to paste at
          const from = editor.view.state.selection.$from;
          const slice = parseFromClipboard(
            editor.view,
            copiedText,
            copiedHtml,
            false, // Whether to copy as plain text or not
            from,
          );
          if (handlePaste(editor.view, new ClipboardEvent('paste'), slice || Slice.empty)) return;

          let singleNode = sliceSingleNode(slice);
          let tr = singleNode
            ? view.state.tr.replaceSelectionWith(singleNode, false)
            : view.state.tr.replaceSelection(slice);
          view.dispatch(tr.scrollIntoView().setMeta('paste', true).setMeta('uiEvent', 'paste'));

          // Return focus back to editor
          editor.view.focus();
        } catch (err) {
          // Display shortcut modal if there are issues reading from clipboard
          // Can occur if user doesn't allow access to clipboard
          globalDispatch?.(setKeyboardShortcutModalOpen(true));
          return;
        }
      },
      label: 'paste',
      icon: <PasteIcon />,
      shortcuts: {
        macOs: ['⌘', '+', 'V'],
        others: ['ctrl', '+', 'V'],
      },
    },
    {
      id: 'insert-col-right',
      label: 'tableWidget.insert-col-right',
      icon: <InsertColumnRightIcon />,
      hasDivider: true,
      onClick: () => {
        editor.chain().focus().addColumnAfter().updateColumnAttrsRight().updateAllCellBorderStyle().run();
        dispatchUpdateWidget();
        fireMPonTableInsert('Column');
      },
      checkIsDisabled: () => !forceEnableAll && isAddColumnDisabled,
    },
    {
      id: 'insert-col-left',
      label: 'tableWidget.insert-col-left',
      icon: <InsertColumnLeftIcon />,
      onClick: () => {
        editor.chain().focus().addColumnBefore().updateColumnAttrsLeft().updateAllCellBorderStyle().run();
        dispatchUpdateWidget();
        fireMPonTableInsert('Column');
      },
      checkIsDisabled: () => !forceEnableAll && isAddColumnDisabled,
    },
    {
      id: 'insert-row-below',
      label: 'tableWidget.insert-row-below',
      icon: <InsertRowBelowIcon />,
      hasDivider: true,
      onClick: () => {
        editor.chain().focus().addRowAfter().updateRowAttrsBelow().updateAllCellBorderStyle().run();
        dispatchUpdateWidget();
        fireMPonTableInsert('Row');
      },
      checkIsDisabled: () => !forceEnableAll && isAddRowDisabled,
    },
    {
      id: 'insert-row-above',
      label: 'tableWidget.insert-row-above',
      icon: <InsertRowAboveIcon />,
      onClick: () => {
        editor.chain().focus().addRowBefore().updateRowAttrsAbove().updateAllCellBorderStyle().run();
        dispatchUpdateWidget();
        fireMPonTableInsert('Row');
      },
      checkIsDisabled: () => !forceEnableAll && isAddRowDisabled,
    },
    {
      id: 'delete-col',
      label: 'tableWidget.delete-col',
      icon: <DeleteColIcon />,
      hasDivider: true,
      onClick: () => {
        editor.chain().focus().deleteColumn().run();
        dispatchUpdateWidget();
        fireMPonTableDelete('Column');
      },
      checkIsDisabled: () => !forceEnableAll && isDeleteColumnDisabled,
    },
    {
      id: 'delete-row',
      label: 'tableWidget.delete-row',
      icon: <DeleteRowIcon />,
      onClick: () => {
        editor.chain().focus().deleteRow().run();
        dispatchUpdateWidget();
        fireMPonTableDelete('Row');
      },
      checkIsDisabled: () => !forceEnableAll && isDeleteRowDisabled,
    },
  ];

  return contextMenuItems;
};
